import {useState} from 'react';
import {useIntl} from 'react-intl';
import {useSelector} from 'react-redux';
import {RTC_PROTOCOL_V1, type CallIntent} from '@phonehq/rtc-core';
import {useAppDispatch, type RootState} from '../../../store/store';
import {showToast} from '../../../store/features/Notifications/notificationsSlice';
import {selectTenant, selectUserId} from '../../../store/features/Auth/authSlice';
import {setCallFrom} from '../../../store/features/calls/dialerSlice';
import {useGetStatusesQuery} from '../../../store/features/Team/teamService';
import {saveDevicePreferences} from '../../groupCalls/services/devicePreferences';
import {getFullName} from '../../../utilis/getFullName';
import {startInternalCall} from '../matrix/matrixActions';
import {closeDirectPrep, promptDndCall, type DirectPrepRequest} from '../store/callSurfaceSlice';
import {PrepDialog, type PrepBanner} from './PrepDialog';
import {usePrepMedia} from './usePrepMedia';
import {useSelfIdentity} from './useSelfIdentity';
import {PRESENCE_TEXT, presenceOf} from './usePrepPresence';
import {prepMessages as m} from './prepMessages';
import {isAddedPersonId, useAddedPeople} from './useAddedPeople';
import {PrepAddPeople} from './PrepAddPeople';
import {startTeamGroupCall} from '../team/startTeamCall';
import {teamCallMessages} from '../team/teamMessages';

export const DirectPrep = ({request, videoAvailable}: {request: DirectPrepRequest; videoAvailable: boolean}) => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const callState = useSelector((state: RootState) => state.dialer.callState);
    const currentUserId = useSelector(selectUserId);
    const tenant = useSelector(selectTenant);
    const [intent, setIntent] = useState<CallIntent>(videoAvailable ? request.intent : 'audio');
    const selfIdentity = useSelfIdentity();
    const media = usePrepMedia(intent, videoAvailable);
    const {peer} = request;
    const name = getFullName(peer.firstName ?? '', peer.lastName ?? '').trim() || String(peer.userId);
    const {data: statuses} = useGetStatusesQuery({users: [Number(peer.userId)]}, {skip: !Number.isFinite(Number(peer.userId))});
    const status = statuses?.[0];
    const presence = presenceOf(status);
    const doNotDisturb = status?.doNotDisturbUserStatus?.enabled === true;
    const added = useAddedPeople();
    const max = RTC_PROTOCOL_V1.maxParticipants;
    // Me, the peer and everyone added from Team.
    const overLimit = added.users.length + 2 > max;
    const [starting, setStarting] = useState(false);

    const banner: PrepBanner | undefined = overLimit
        ? {tone: 'warning', title: intl.formatMessage(m.limitTitle), body: intl.formatMessage(m.limitBody, {max}), testId: 'group-call-lobby-full'}
        : media.issues.microphone
        ? {
              tone: 'warning',
              title: intl.formatMessage(m.micBlockedTitle),
              body: intl.formatMessage(m.micBlockedBody),
              action: {label: intl.formatMessage(m.micHowTo), onClick: media.retryMicrophone},
              testId: 'group-call-lobby-device-issues',
          }
        : undefined;

    const close = () => dispatch(closeDirectPrep());

    const startGroup = async () => {
        setStarting(true);
        const outcome = await startTeamGroupCall([Number(peer.userId), ...added.users.map(user => Number(user.userId ?? user.id))], intent);
        setStarting(false);
        if (outcome === 'failed') {
            dispatch(showToast({description: intl.formatMessage(teamCallMessages.failed), type: 'error'}));
            return;
        }
        close();
    };

    const start = () => {
        saveDevicePreferences(media.devices);
        if (added.users.length > 0) {
            void startGroup();
            return;
        }
        close();
        if (doNotDisturb) {
            dispatch(setCallFrom(peer));
            dispatch(promptDndCall({video: intent === 'video', roomId: request.roomId}));
            return;
        }
        void startInternalCall(peer, {
            video: intent === 'video',
            roomId: request.roomId,
            callState,
            currentUserId: String(currentUserId ?? ''),
            tenant: String(tenant ?? ''),
            dispatch,
        });
    };

    return (
        <PrepDialog
            subtitle={intl.formatMessage(m.directSubtitle, {name})}
            intent={intent}
            videoAvailable={videoAvailable}
            onIntent={setIntent}
            people={[{
                id: String(peer.userId),
                identity: {name, userId: String(peer.userId), avatarUrl: peer.avatarUrl || undefined},
                presence: intl.formatMessage(doNotDisturb ? m.presenceDnd : PRESENCE_TEXT[presence]),
                selected: true,
                selectable: false,
            }, ...added.people]}
            onTogglePerson={id => isAddedPersonId(id) && added.remove(id)}
            addPeople={<PrepAddPeople excludeUserIds={new Set([String(peer.userId), ...added.ids])} onAdd={added.add}/>}
            microphone={media.microphone}
            camera={media.camera}
            preview={media.preview}
            selfIdentity={selfIdentity}
            banner={banner}
            primary={{label: intl.formatMessage(m.start), busy: starting, disabled: callState !== 'IDLE' || overLimit, onClick: start}}
            onCancel={close}
        />
    );
};
